import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import {
  JourneyChangePayload,
  JourneyFormSubmittedValues,
} from '../../../types/journey';
import JourneyEditDialog from './JourneyEditDialog';
import JourneyForm from './JourneyForm';
import { JourneyEditDialogState, JourneyTemplateSubPageContext } from './types';
import { getChangeReason } from './utils';
import { DAYJS_DATA_TIME_FORMAT } from '../../../utils/date';
import { EntityStatus } from '../../../types/core';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const EditJourneyPage = () => {
  /** This page must be able to handle both the edition of existing Journey entities
   * or the "edition" (actual creation) of new Journey entities based on placeholder information based on a route.
   */
  const {
    journey,
    journeyId,
    setJourneyNameByDateAndTime,
    isFetching,
    error,
    refetchJourney,
    updateContextJourney,
    notesManagement: { refetch: refetchNotes },
  } = useOutletContext<JourneyTemplateSubPageContext>();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState<JourneyEditDialogState>(null);

  const { rescheduleJourney } = useIotsApi();
  const rescheduleMutation = useMutation({
    mutationFn: (journeychangePayload: JourneyChangePayload) =>
      rescheduleJourney(journeychangePayload),
  });

  const handleFormSubmit = (
    newValues: JourneyFormSubmittedValues,
    callback: () => void,
  ) => {
    setDialogState({
      changeReason: getChangeReason(journey, newValues),
      newValues,
      callback,
    });
  };

  const handleDialogClose = () => {
    if (rescheduleMutation.isPending) {
      return;
    }
    setDialogState(null);
  };

  const handleDialogConfirm = (note: string) => {
    if (dialogState) {
      rescheduleMutation.mutate(
        {
          journeyId: journeyId || null,
          journey: {
            operatorId: journey.operatorId,
            contractId: journey.contractId || null,
            routeId: journey.routeId || null,
            serviceType: journey.serviceType,
            arrivalPortId: dialogState.newValues.arrivalPortId,
            departurePortId: dialogState.newValues.departurePortId,
            day: journey.day,
            time: dialogState.newValues.time.format(DAYJS_DATA_TIME_FORMAT),
          },
          note: {
            note,
            type: dialogState.changeReason.noteType,
            reliefEventType: null,
          },
        },
        {
          onSuccess: newJourney => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.journey-edition-success'),
            });
            if (!journeyId) {
              void disableNavigationGuard(() =>
                navigate(`/admin/journeys/${newJourney.journey.id}/`),
              );
            } else {
              updateContextJourney({
                ...newJourney.journey,
                contractId: newJourney.journey.contractId,
                routeId: newJourney.journey.routeId,
              });
              refetchJourney();
              refetchNotes();
              dialogState.callback();
            }
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(
                error,
                t('content.journey-edition-error'),
              ),
            });
            rescheduleMutation.reset();
          },
        },
      );
    }
    setDialogState(null);
  };

  return (
    <>
      <JourneyForm
        onSubmit={handleFormSubmit}
        journey={journey}
        isLoading={isFetching || rescheduleMutation.isPending}
        disabled={Boolean(
          error ||
            [EntityStatus.Submitted, EntityStatus.Invoiced].includes(
              journey.entityStatus,
            ),
        )}
        onDayOrTimeChange={setJourneyNameByDateAndTime}
      />
      <JourneyEditDialog
        dialogState={dialogState}
        loading={isFetching || rescheduleMutation.isPending}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
      />
    </>
  );
};

export default EditJourneyPage;
